import GL from "_src/engine/gl";
import events from "_src/engine/events";
import output from "_src/engine/output";
import renderer from "_src/engine/renderer";
import workerPool from "_src/engine/worker_pool";
import workerManager from "_src/engine/worker_manager.js";

import workspace from "_src/workspace";
import appState from "_src/app_state";
import config from "_src/config";

import windowManager from "_src/client/window_manager";
import {screenUtils} from "_src/client/utils";
import viewcube from "_src/client/viewcube";
import inputCollector from "_src/client/input";
import compat from "_src/engine/compat";
import commonAPI from "_src/common_api";
import {msgType, objectType} from "_src/constants";
import {quat} from "_src/libs/gl-matrix";
import renderGraph from "_src/render_graph/render_graph";
import {drawMode} from "_src/engine/constants";
import takeScreenshot from "_src/client/screenshot";
import projectTree from "_src/reformer/project_tree";
import {cleanupMaterialsResources} from "_src/client/materials/material";

let _initReject = null;
let _initialized = false;
let _animationFrameRequestID = -1;
let _lastTime = 0;

const commonFacadeAPI = {
  /** Objects rendering mode "Solid"
   * @memberof WS
   * @member RM_SOLID
   */
  RM_SOLID: appState.RM_SOLID,

  /** Objects rendering mode "Wireframe"
   * @memberof WS
   * @member RM_WIREFRAME
   */
  RM_WIREFRAME: appState.RM_WIREFRAME,

  /** Objects rendering mode "Points"
   * @memberof WS
   * @member RM_POINTS
   */
  RM_POINTS: appState.RM_POINTS,

  /** Objects color mode "Texture"
   * @memberof WS
   * @member CM_TEXTURE
   */
  CM_TEXTURE: appState.CM_TEXTURE,

  /** Objects color mode "Scan color"
   * @memberof WS
   * @member CM_SCAN_COLOR
   */
  CM_SCAN_COLOR: appState.CM_SCAN_COLOR,

  /**
   * Set viewcube scene size and position
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Number} params.rightOffset distance in pixels from right screen side to left viewcube scene side
   * @param {Number} params.topOffset distance in pixels from top screen side to bottom viewcube scene side
   * @param {Number} params.width viewcube scene width in pixels
   * @param {Number} params.height viewcube scene height in pixels
   * @method viewcubeSetAppearance
   */
  viewcubeSetAppearance({rightOffset, topOffset, width, height}) {
    const viewport = [rightOffset, topOffset, width, height];
    const phyViewport = screenUtils.viewportToPhisycalSize(viewport, []);
    const oldPhyViewport = viewcube.getScreenAppearance();

    if (quat.equals(phyViewport, oldPhyViewport)) {
      return;
    }

    viewcube.setScreenAppearance(phyViewport[0], phyViewport[1], phyViewport[2], phyViewport[3]);
    events.emit("frame_requested");
  },

  /**
   * Subscribe to Web Studio event
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {string} params.event Event to subscribe to
   * @param {Function} params.callback Callback to be called on this event. Parameter is of {@link Application.State} type.
   * @method on
   */
  on({event, callback}) {
    events.on(event, callback);
  },

  /**
   * Unsubscribe from Web Studio event
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {string} params.event Event to unsubscribe from
   * @param {Function} params.callback Callback which is called on this event. Parameter is of {@link Application.State} type.
   * @method off
   */
  off({event, callback}) {
    events.off(event, callback);
  },

  /**
   * Check if WS is available
   * @memberof WS
   * @method isAvailable
   * @returns {Boolean} is WS available
   */
  isAvailable() {
    return _initialized;
  },

  /**
   * Set the background color
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {{ r, g, b }} params.color Object color. Should be object with values of red, green and blue. Each component should be in range [0..1]
   * @method setBackgroundColor
   */
  setBackgroundColor({color}) {
    config.backgroundColor.r = color.r;
    config.backgroundColor.g = color.g;
    config.backgroundColor.b = color.b;

    windowManager.applyToAllWindows((window) => {
      window.setBackgroundColor(color.r, color.g, color.b, 0);
      window.renderGraph.requestFrame();
    });
  },

  /**
   * Take a screenshot. If no parameters provided, canvas resolution is used
   * @memberof WS
   * @param {Object} params Method parameters
   * @param {Array(2)} [params.size] Resolution [width, height] of the rendered image. It has priority over rFactor
   * @param {Number} [params.rFactor] Resolution factor of the image calculated from the original canvas size
   * @returns {Promise} Promise resolved when the pixels are read. Returns pixels' buffer
   * @method takeScreenshot
   */
  async takeScreenshot(params = {}) {
    const camera = workspace.getScene().camera;
    let width = camera.viewport[2];
    let height = camera.viewport[3];

    if (params.size !== undefined) {
      width = params.size[0];
      height = params.size[1];
    } else if (params.rFactor !== undefined) {
      width *= params.rFactor;
      height *= params.rFactor;
    }

    width = Math.round(width);
    height = Math.round(height);

    const maxFbSize = GL.getMaxRenderBufferSize();

    if (width > maxFbSize || height > maxFbSize) {
      width = Math.min(width, maxFbSize);
      height = Math.min(height, maxFbSize);
      output.warn(`Reached hardware limits. Reducing screenshot size to ${width} x ${height}`);
    }

    const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
    const pixels = await takeScreenshot(mainRenderGraph, width, height);
    mainRenderGraph.requestFrame();

    return pixels;
  },

  /**
   * This is debug method to emulate WebGL context losing
   * @memberof WS
   * @method loseWebGLContext
   */
  loseWebGLContext() {
    GL.loseContext();
  },
}

const commonFacadeFunctions = {
  init(canvas) {
    return new Promise((resolve, reject) => {

      _initReject = reject;

      const promises = [];

      output.setLogType(output.BROWSER_LOG);

      appState.init();

      config.useWorkers = workerManager.checkWorkersSupport();

      compat.collectSystemData();

      commonFacadeFunctions.resize(canvas);

      const { width, height } = screenUtils.getCanvasPhisycalSize(canvas);
      windowManager.init();
      windowManager.createWindow([0, 0, width, height]);
      const mainWindow = windowManager.getWindow(windowManager.MAIN);
      const mainRenderGraph = mainWindow.renderGraph;

      const workspaceScene = mainRenderGraph.getWorkspaceScene();
      const camera = workspaceScene.camera;

      initCommonEvents(canvas);

      workspace.setScene(workspaceScene);

      promises.push(viewcube.init());

      workerPool.init();
      renderer.init();

      viewcube.update(camera);

      Promise.all(promises).then(() => {
        _initialized = true;
        _initReject = null;
        resolve();
      }).catch((error) => {
        output.error(output.DEVELOPERS_ERROR, "Couldn't initialize application. " + error);
      });

    });

  },

  startRenderingCycle(canvas, derivedFrameFunction, derivedResizeFunction) {
    derivedResizeFunction(canvas);

    if (_animationFrameRequestID === -1) {

      _animationFrameRequestID = window.requestAnimationFrame(derivedFrameFunction);

    }
  },

  async loadProject(params) {

    const workspaceScene = workspace.getScene();
    if (!workspaceScene) {
      output.error(output.API_ERROR, "Couldn't load the project. Call WS.init() first!");
      return;
    }

    await commonAPI.closeProject();

    events.emit("user_action", {type: msgType.LOAD_PROJECT, payload: params});

    let json;
    try {
      json = await workspace.openProject(params.path);
      if (!json) {
        return null;
      }
      workspace.prepareProjectLoading(json);

    } catch(error) {

      output.error(output.DEVELOPERS_ERROR, "Can't load project. " + error);
      return null;

    }

    return json;
  },

  destroy(canvas) {

    terminateInitialization();

    if (!workspace.getScene()) {
      output.error(output.API_ERROR, "Couldn't destroy the application. Call WS.init() first!");
      return;
    }

    window.cancelAnimationFrame(_animationFrameRequestID);
    _animationFrameRequestID = -1;

    commonAPI.closeProject();
    commonAPI.cleanup();

    windowManager.cleanup();
    events.cleanup(canvas);
    inputCollector.cleanup(canvas);
    viewcube.cleanup();

    appState.cleanup();
    cleanupMaterialsResources();

    GL.cleanup();

    _initialized = false;

  },

  frame(time) {

    if(_animationFrameRequestID === -1) {
      return null;
    }

    const t = time/1000;

    const dt = t - _lastTime;
    _lastTime = t;

    viewcube.updateAnimation(dt);

    windowManager.update(dt);

    projectTree.checkForUpdates();

    return {t, dt};
  },

  resize(canvas) {
    const { width, height } = screenUtils.getCanvasPhisycalSize(canvas);

    canvas.width = width;
    canvas.height = height;

    windowManager.resizeWindows(width, height);
  },

  synchronizeApplicationState() {
    const renderMode = appState.getStateValue("renderMode");
    const colorMode = appState.getStateValue("colorMode");

    {
      const wireframeEnabled = renderMode === appState.RM_WIREFRAME && colorMode != appState.CM_XRAY;
      const mainRenderGraph = windowManager.getWindow(windowManager.MAIN).renderGraph;
      mainRenderGraph.setEnabled(renderGraph.WIREFRAME, wireframeEnabled);
    }

    windowManager.applyToAllWindows((window) => {
      window.renderGraph.requestFrame();
    });
  },

  synchronizeObjectsState() {
    const renderMode = appState.getStateValue("renderMode");
    const colorMode = appState.getStateValue("colorMode");

    for (const object of workspace.getObjects()) {
      const shader = object.model.shader;

      {
        const wireframeEnabled = renderMode === appState.RM_WIREFRAME && colorMode != appState.CM_XRAY;
        const transparent = false;
        workspace.prepareWireframeGeometry(object, wireframeEnabled);
        shader.setWireframeEnabled(wireframeEnabled, transparent);
      }

      {
        object.updateTextureUsage();
      }
    }
  },

  synchronizeShaderState(object) {
    const renderMode = appState.getStateValue("renderMode");

    {
      const forcePointsRenderMode = object.type === objectType.SURFELS;
      const pointsRenderMode = forcePointsRenderMode || renderMode === appState.RM_POINTS;
      const renderingMode = pointsRenderMode ? drawMode.POINTS : drawMode.TRIANGLES;

      for (const frame of object.frames) {
        frame.model.setDrawMode(renderingMode);
      }
      object.model.shader.setDrawMode(renderingMode);
    }
  },
};

export {commonFacadeAPI, commonFacadeFunctions};

function terminateInitialization() {
  if (_initReject) {
    _initReject("Initialization has been terminated.");
  }
}

function initCommonEvents(canvas) {
  events.initBrowserEvents(canvas);
  inputCollector.init(canvas);

  events.on("objects_updated", (objects) => {
    for (const object of objects) {
      object.model.fixFacesInversion();
    }
  });

  events.on("frame_requested", () => {
    windowManager.updateWindows();
  });

  events.on("camera_move", (camera) => {

    if (camera !== workspace.getScene().camera) {
      return;
    }

    viewcube.update(camera);
  });

  events.on("shift_loading_queue", (queue) => {
    workspace.shiftLoadingQueue(queue);
  });

  events.on("object_added", (object) => {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    mainWindow.addObject(object);
  });

  events.on("object_removed", (object) => {
    const mainWindow = windowManager.getWindow(windowManager.MAIN);
    mainWindow.removeObject(object);
  });

  events.on("objects_initialized", () => {
    windowManager.applyToAllWindows(window => window.updateObjectsScene());
  });

  events.on("loadingProgress", () => {
    windowManager.updateWindows();
  });

}
